import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import get from 'lodash/get';
import styles from 'feature/street-parking/index.module.sass';
import {
  useStreetParkingContext,
  SET_NO_CREDIT_CARD_DIALOG,
  SET_NO_LICENSE_PLATE_DIALOG,
  HIDE_TERMS_OF_TAIPEI_SERVICE_DIALOG,
} from 'feature/street-parking/use-street-parking';
import classNames from 'classnames/bind';
import {
  useAppContext,
  NOW_SETTING_LICENSE_PLATE_AND_CREDIT_CARD,
  NOW_SETTING_LICENSE_PLATE,
  NOW_SETTING_CREDIT_CARD,
  SHOW_TOAST_MESSAGE,
} from 'use-app';
import PageLayout from 'layout/page';
import StreetParkingLayout from 'layout/street-parking';
import CardItemSwitch from 'components/card-item-switch';
import LoadingDialog from 'components/loading-dialog';
import ButtonDialog from 'components/button-dialog';
import { PATH } from 'config';
import amplitude from 'service/amplitude';
import dayjs from 'dayjs';
import { getUserInfo } from 'helpers/data-check-helper';
import { getLoginState } from 'model/user';
import { distributorName } from 'config';
import { getLineBankIsBinded } from 'service/local-data-provider';
import { isLineBank } from 'config';
import iconOrder from 'assets/order.svg';
import Card from 'components/card';

const cx = classNames.bind(styles);

const OnStreetParking = () => {
  const history = useHistory();
  const { state: appState, dispatch: appDispatch } = useAppContext();
  const { state, dispatch, switchStreetParkingAutoPay, fetchAutopayBoard } = useStreetParkingContext();
  const { hasPayments, hasLicensePlates, hasStreetParkingAutoPay } = getUserInfo(appState);
  const enableAutoPayUpdatedAt = get(appState, 'me.enableOnStreetParkingAutoPayUpdatedAt');
  const autoPayStreetParkingCities = get(appState, 'bootstraps.autoPayStreetParkingBillsCities', []);
  const [checked, setChecked] = useState(false);
  const loginState = getLoginState();
  const isUser = loginState === 'user';

  const linkToLogin = () => {
    appDispatch({
      type: SHOW_TOAST_MESSAGE,
      payload: '請先登入再繼續',
    });
    history.push(PATH.USER_LOGIN);
  };

  useEffect(() => {
    if (isLineBank) {
      let linebankStreetParkingAutopayIsAbleToUse = getLineBankIsBinded() && hasPayments;
      setChecked(hasStreetParkingAutoPay && linebankStreetParkingAutopayIsAbleToUse);
    } else {
      setChecked(hasStreetParkingAutoPay);
    }
  }, [appState, hasStreetParkingAutoPay, hasPayments]);

  useEffect(() => {
    fetchAutopayBoard();
  }, [fetchAutopayBoard]);

  const openNoLicensePlateDialog = () => {
    dispatch({
      type: SET_NO_LICENSE_PLATE_DIALOG,
      payload: true,
    });
  };

  const closeNoLicensePlateDialog = () => {
    dispatch({
      type: SET_NO_LICENSE_PLATE_DIALOG,
      payload: false,
    });
  };

  const openNoCreditCardDialog = () => {
    dispatch({
      type: SET_NO_CREDIT_CARD_DIALOG,
      payload: true,
    });
  };

  const closeNoCreditCardDialog = () => {
    dispatch({
      type: SET_NO_CREDIT_CARD_DIALOG,
      payload: false,
    });
  };

  const setLicensePlateAndCreditCard = () => {
    closeNoLicensePlateDialog();
    appDispatch({
      type: NOW_SETTING_LICENSE_PLATE_AND_CREDIT_CARD,
      payload: {
        noPlateNoCard: true,
        from: 'autopay',
      },
    });
    history.push(PATH.LICENSE_PLATES_NEW);
  };

  const setLicensePlate = () => {
    closeNoLicensePlateDialog();
    appDispatch({
      type: NOW_SETTING_LICENSE_PLATE,
      payload: {
        noPlate: true,
        from: 'autopay',
      },
    });
    history.push(PATH.LICENSE_PLATES_NEW);
  };

  const setCreditCard = () => {
    closeNoCreditCardDialog();
    appDispatch({
      type: NOW_SETTING_CREDIT_CARD,
      payload: {
        noCard: true,
        from: 'autopay',
      },
    });
    history.push(PATH.MOCHIPAY_FLOW_SETTING);
  };

  const switchState = () => {
    if (hasStreetParkingAutoPay && hasPayments) {
      return 'on';
    } else if (hasStreetParkingAutoPay) {
      return 'alert';
    } else {
      return 'off';
    }
  };

  const SubText = () => {
    const activate = () => {
      if (!isUser) return linkToLogin();

      if (!hasLicensePlates) {
        openNoLicensePlateDialog();
      } else {
        openNoCreditCardDialog();
      }
    };

    if (hasStreetParkingAutoPay && (!hasPayments || !hasLicensePlates)) {
      return (
        <div onClick={activate} className={cx('sub-text', 'complete')}>
          完成設定啟用繳費
        </div>
      );
    } else if (!hasStreetParkingAutoPay && (!hasPayments || !hasLicensePlates)) {
      return (
        <div onClick={activate} className={cx('sub-text', 'start')}>
          點此啟用設定
        </div>
      );
    } else {
      let { lastFourCode } = appState.me.currentCreditCard;
      if (lastFourCode) {
        return (
          <div className={styles['last-four-code']}>
            <span>···· </span>
            {appState.me.currentCreditCard.lastFourCode}
          </div>
        );
      } else {
        return `已綁定『${distributorName()}」扣款`;
      }
    }
  };

  const SupportCities = () => {
    const cities = autoPayStreetParkingCities.map(city => {
      return city.name;
    });

    let alt =
      '基隆市、台北市、新北市、桃園市、新竹縣、新竹市、苗栗市、台中市、彰化縣、台南市、高雄市、花蓮縣、台東縣、澎湖縣、宜蘭縣、嘉義市';

    return (
      <>
        <div>支援繳費縣市</div>
        <div className={styles['support-cities']}>
          {(cities || alt).map(city => (
            <div className={styles.city}>{city}</div>
          ))}
        </div>
      </>
    );
  };

  const switchCheck = () => {
    if (!isUser) return linkToLogin();

    if (hasStreetParkingAutoPay) {
      amplitude.onStreetParking_disable_onClick();
      switchStreetParkingAutoPay(appState);
    } else {
      if (hasPayments && hasLicensePlates) {
        amplitude.onStreetParking_enable_onClick();
        switchStreetParkingAutoPay(appState);
      } else {
        if (!hasLicensePlates) {
          openNoLicensePlateDialog();
        } else {
          openNoCreditCardDialog();
        }
      }
    }
  };

  const onSearchBills = () => {
    if (!isUser) return linkToLogin();

    amplitude.onStreetParking_inquire_onClick();
    history.push(PATH.STREET_PARKING_CITIES);
  };

  const Board = () => {
    const { autopayBoard } = state;
    if (!autopayBoard) return <></>;
    return (
      <div style={{ color: autopayBoard.attributes.titleColor }} className={styles.board}>
        {autopayBoard.attributes.title}
      </div>
    );
  };

  return (
    <>
      <LoadingDialog isShow={state.loading} />
      <ButtonDialog
        isShow={state.showTermOfTaipeiServiceDialog}
        horizonal
        title="路停自動繳提醒"
        items={[
          {
            theme: 'positive',
            content: '查詢停車單繳費',
            onClick: () => {
              amplitude.onStreetParking_notice_inquire_onClick();
              dispatch({ type: HIDE_TERMS_OF_TAIPEI_SERVICE_DIALOG });
              history.push(PATH.STREET_PARKING_CITIES);
            },
          },
          {
            theme: 'cancel',
            content: '稍後',
            onClick: () => {
              amplitude.onStreetParking_notice_inquire_onClose();
              dispatch({ type: HIDE_TERMS_OF_TAIPEI_SERVICE_DIALOG });
            },
          },
        ]}
      >
        您已成功設定自動繳。提醒您，因政府規範，啟用日 {dayjs(enableAutoPayUpdatedAt).format('MM.DD')}{' '}
        前的繳費單（含催繳單、逾期單）無法自動扣繳，請手動查詢停車單繳費。
      </ButtonDialog>

      <ButtonDialog
        isShow={state.setNoLicensePlateDialog}
        title={!hasPayments ? '簡單兩步驟，開通麻吉付' : '差一點就完成'}
        horizonal
        items={[
          {
            content: !hasPayments ? '立即開通麻吉付' : '立即設定付款車牌',
            theme: 'positive',
            onClick: !hasPayments ? setLicensePlateAndCreditCard : setLicensePlate,
          },
          {
            content: '稍後',
            theme: 'cancel',
            onClick: closeNoLicensePlateDialog,
          },
        ]}
      >
        {!hasPayments
          ? `完成付款方式及車牌設定，立即開通麻吉付，享受便捷的行動支付功能！`
          : `完成付款車牌設定，立即開通麻吉付，享受便捷的行動支付功能！`}
      </ButtonDialog>

      <ButtonDialog
        isShow={state.setNoCreditCardDialog}
        title="差一點就完成"
        horizonal
        items={[
          {
            content: '立即設定付款方式',
            theme: 'positive',
            onClick: setCreditCard,
          },
          {
            content: '稍後',
            theme: 'cancel',
            onClick: closeNoCreditCardDialog,
          },
        ]}
      >
        完成付款方式設定，立即開通麻吉付，享受便捷的行動支付功能！
      </ButtonDialog>
      <PageLayout>
        <Board />
        <StreetParkingLayout
          cities={appState.bootstraps?.autoPayStreetParkingBillsCities}
          className={styles.autopay}
          helpOnClick={() => {
            amplitude.onStreetParking_help_onClick();
          }}
        >
          <div>
            <div className={styles.title}>一鍵立即生效，帳單不再漏繳</div>
            <CardItemSwitch
              title="路邊停車自動繳費"
              switchState={switchState()}
              subText={<SubText />}
              isChecked={checked}
              onChange={switchCheck}
              hasPayments={hasPayments}
              enableAutoPayUpdatedAt={enableAutoPayUpdatedAt}
            />
            <Card className={styles.searchBill} onClick={onSearchBills}>
              <img src={iconOrder} alt="order" />
              查詢繳費
            </Card>
            <SupportCities />
          </div>
        </StreetParkingLayout>
      </PageLayout>
    </>
  );
};

export default OnStreetParking;
